import { useNavigate, useParams } from 'react-router-dom';
import PageForm from '../../templates/PageForm';
import CardList from '../../organisms/CardList';
import APagination from '../../molecules/APagination';
import useFetch from '../../../hooks/useFetch';
import { FaCalendarAlt, FaBuilding, FaFileExcel, FaUser, FaClock } from 'react-icons/fa';
import appConfig from '../../../config/app-config';

const DATA_PROVIDER_ENV = appConfig.apis.dataProviderApiEnv;
const BASE_URL = `${DATA_PROVIDER_ENV.address}/annual-provider-reports/page-list`;
const API_KEY = DATA_PROVIDER_ENV.key;

interface AnnualProviderReport {
    id: string;
    provider: string;
    year: string;
    user_id: string;
    created_at: string;
    processing_date: string;
    total_documents: string;
    social_id_count: string;
    gov_tax_id_count: string;
    s3_file_key: string;
}

const CardSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
            <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
            <div className="h-8 w-8 bg-gray-300 dark:bg-gray-600 rounded"></div>
        </div>
        <div className="space-y-3">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-16"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-28"></div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-20"></div>
        </div>
    </div>
);

const AnnualProviderReportPageList = () => {
    const navigate = useNavigate();
    const {
        page = '0',
        pageSize = '20',
        field: fieldSorted = 'year',
        order: orderSorted = 'DESC',
        filter,
    } = useParams();

    let provider = '';
    if (filter) {
        [provider = ''] = filter.match(/=(.*?);/g)?.map((m: string) => m.slice(1, -1)) ?? [''];
    }

    const url = `${BASE_URL}?search=${provider}&page=${page}&pageSize=${pageSize}&order=${fieldSorted},${orderSorted}`;
    const { isLoading, data, error, isTokenError } = useFetch(url, [{ key: 'x-api-key', value: API_KEY }]);

    if (isTokenError) navigate('/denied');
    if (error) navigate('/network-error');

    const pageChanged = (p: number) => {
        navigate(`/data-provider/annual-provider-reports/${p - 1}/${pageSize}/${fieldSorted}/${orderSorted}${filter ? `/${filter}` : ''}`, { replace: true });
    };

    const downloadReport = async (year: string, provider: string) => {
        try {
            const downloadUrl = `${DATA_PROVIDER_ENV.address}/annual-provider-reports/${year}/${provider}/download`;
            const response = await fetch(downloadUrl, {
                headers: {
                    'x-api-key': API_KEY,
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const jsonResponse = await response.json();

                // Decode base64 to binary
                const base64Data = jsonResponse.body;
                const binaryString = atob(base64Data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                // Create blob and download
                const blob = new Blob([bytes], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `annual-report-${provider}-${year}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }
        } catch (error) {
            console.error('Erro ao baixar relatório:', error);
        }
    };

    const renderCard = (item: AnnualProviderReport, index: number) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <FaCalendarAlt className="mr-2 text-blue-500" />
                    {item.year}
                </h3>
                <button
                    type="button"
                    className="text-white bg-blue-500 hover:bg-blue-700 font-medium rounded text-sm px-4 py-2 flex items-center"
                    onClick={() => downloadReport(item.year, item.provider)}
                >
                    <FaFileExcel className="mr-2" />
                    Exportar
                </button>
            </div>

            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex items-center">
                    <FaBuilding className="mr-2 text-gray-400 w-4" />
                    <span className="font-medium">{item.provider}</span>
                </div>

                <div className="flex items-center">
                    <FaUser className="mr-2 text-gray-400 w-4" />
                    <span>Usuário: {item.user_id}</span>
                </div>

                <div className="grid grid-cols-3 gap-4 text-xs">
                    <div className="text-center">
                        <div className="font-semibold text-gray-900 dark:text-white">{item.total_documents}</div>
                        <div className="text-gray-500">Total relatórios</div>
                    </div>
                    <div className="text-center">
                        <div className="font-semibold text-gray-900 dark:text-white">{item.social_id_count}</div>
                        <div className="text-gray-500">Relatórios CPF</div>
                    </div>
                    <div className="text-center">
                        <div className="font-semibold text-gray-900 dark:text-white">{item.gov_tax_id_count}</div>
                        <div className="text-gray-500">Relatórios CNPJ</div>
                    </div>
                </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center">
                    <FaClock className="mr-1 w-3" />
                    <span>Criado: {new Date(item.created_at).toLocaleString('pt-BR')}</span>
                </div>
                <div className="flex items-center">
                    <FaClock className="mr-1 w-3" />
                    <span>Processado: {new Date(item.processing_date).toLocaleString('pt-BR')}</span>
                </div>
            </div>
        </div>
    );

    const renderSkeletonCards = () => (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Array.from({ length: 6 }).map((_, index) => (
                <CardSkeleton key={index} />
            ))}
        </div>
    );

    const items: AnnualProviderReport[] = data?.list || [];

    return (
        <PageForm
            pageTitle="Relatórios Anuais"
            apiVersion="v1"
            isLoading={isLoading}
            breadcrumb={[{ label: 'Relatórios Anuais', url: '#' }]}
            controlSlot={
                <div className="flex justify-end">
                    {/* Removido PageSearch conforme solicitado */}
                </div>
            }
        >
            {isLoading ? (
                renderSkeletonCards()
            ) : (
                <>
                    <CardList
                        items={items}
                        renderCard={renderCard}
                        emptyMessage="Nenhum relatório anual encontrado"
                        columns={2}
                    />

                    {data?.totalPages > 1 && (
                        <div className="mt-6">
                            <APagination
                                currentPage={parseInt(page) + 1}
                                totalPages={data.totalPages}
                                onPageChange={pageChanged}
                            />
                        </div>
                    )}
                </>
            )}
        </PageForm>
    );
};

export default AnnualProviderReportPageList;
